import { parseComposition, type CompositionDocument } from './document';
import { listCompositions } from './storage';
import {
  acknowledgeSync,
  nextSyncRequest,
  preserveSyncConflict,
  receiveComposition,
  type SyncRequest,
} from './syncStorage';

export type CloudStatus = 'local' | 'syncing' | 'synced' | 'offline' | 'error';
export type CompositionApi = {
  list: (
    after?: string,
  ) => Promise<{ items: { id: string; revision: number }[]; nextCursor: string | null }>;
  get: (id: string) => Promise<CompositionDocument>;
  history: (id: string) => Promise<CompositionDocument[]>;
  put: (request: SyncRequest) => Promise<CompositionDocument>;
};
export class CompositionConflict extends Error {
  constructor(readonly document: CompositionDocument) {
    super('Another device saved a different version.');
  }
}

export async function syncCompositions(
  ownerId: string,
  api: CompositionApi,
  cancelled: () => boolean = () => false,
) {
  const conflicts: { id: string; copyId: string }[] = [];
  const verify = (document: CompositionDocument, id: string) => {
    parseComposition(document);
    if (document.ownerId !== ownerId || document.id !== id || document.revision < 1)
      throw new Error('Unexpected composition sync response.');
    return document;
  };
  // Two passes also upload a conflict copy generated during the first pass.
  for (let pass = 0; pass < 2; pass++) {
    for (const document of await listCompositions(ownerId)) {
      if (cancelled()) return { conflicts, pending: true };
      const request = await nextSyncRequest(ownerId, document.id);
      if (!request) continue;
      try {
        const remote = verify(await api.put(request), document.id);
        if (cancelled()) return { conflicts, pending: true };
        await acknowledgeSync(request, remote);
      } catch (error) {
        if (!(error instanceof CompositionConflict)) throw error;
        if (cancelled()) return { conflicts, pending: true };
        const copyId = await preserveSyncConflict(request, verify(error.document, document.id));
        if (copyId) conflicts.push({ id: document.id, copyId });
      }
    }
  }
  let cursor: string | undefined;
  const cursors = new Set<string>();
  do {
    if (cancelled()) return { conflicts, pending: true };
    const page = await api.list(cursor);
    const local = new Map(
      (await listCompositions(ownerId)).map((document) => [document.id, document]),
    );
    for (const item of page.items) {
      if (cancelled()) return { conflicts, pending: true };
      if ((local.get(item.id)?.cloud?.revision ?? 0) < item.revision) {
        const remote = verify(await api.get(item.id), item.id);
        if (cancelled()) return { conflicts, pending: true };
        await receiveComposition(ownerId, remote);
      }
    }
    cursor = page.nextCursor ?? undefined;
    if (cursor && cursors.has(cursor))
      throw new Error('The composition library returned a repeated page. Retry sync.');
    if (cursor) cursors.add(cursor);
  } while (cursor);
  const pending = (await listCompositions(ownerId)).some(
    (document) => document.revision !== document.cloud?.syncedLocalRevision,
  );
  return { conflicts, pending };
}
